'use client';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _extends from "@babel/runtime/helpers/esm/extends";
const _excluded = ["className", "classes"],
  _excluded2 = ["localeText", "resourceColumnLabel", "apiRef"];
import * as React from 'react';
import clsx from 'clsx';
import { styled, useThemeProps } from '@mui/material/styles';
import { useLicenseVerifier, Watermark } from '@mui/x-license/internals';
import composeClasses from '@mui/utils/composeClasses';
import { useExtractEventTimelinePremiumParameters, useEventTimelinePremium } from '@mui/x-scheduler-headless-premium/use-event-timeline-premium';
import { SchedulerStoreContext } from '@mui/x-scheduler-headless/use-scheduler-store-context';
import { useInitializeApiRef } from '@mui/x-scheduler-headless/internals';
import { eventDialogSlots, EventDialogStyledContext, EVENT_TIMELINE_DEFAULT_LOCALE_TEXT } from '@mui/x-scheduler/internals';
import { EventTimelinePremiumContent } from "./content/index.mjs";
import { getEventTimelinePremiumUtilityClass } from "./eventTimelinePremiumClasses.mjs";
import { EventTimelinePremiumStyledContext } from "./EventTimelinePremiumStyledContext.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const packageInfo = {
  releaseDate: "MTc3NDU2OTYwMDAwMA==",
  version: "0.0.1-alpha.0",
  name: 'x-scheduler-premium'
};
const watermark = /*#__PURE__*/_jsx(Watermark, {
  packageInfo: packageInfo
});
const useUtilityClasses = classes => {
  const slots = _extends({
    root: ['root'],
    content: ['content'],
    grid: ['grid'],
    headerRow: ['headerRow'],
    titleHeaderCell: ['titleHeaderCell'],
    eventsHeaderCell: ['eventsHeaderCell'],
    eventsHeaderCellContent: ['eventsHeaderCellContent'],
    titleSubGrid: ['titleSubGrid'],
    eventsSubGridWrapper: ['eventsSubGridWrapper'],
    eventsSubGrid: ['eventsSubGrid'],
    eventsSubGridRow: ['eventsSubGridRow'],
    titleCellRow: ['titleCellRow'],
    titleCell: ['titleCell'],
    titleCellLegendColor: ['titleCellLegendColor'],
    currentTimeIndicator: ['currentTimeIndicator'],
    currentTimeIndicatorCircle: ['currentTimeIndicatorCircle'],
    event: ['event'],
    eventPlaceholder: ['eventPlaceholder'],
    eventResizeHandler: ['eventResizeHandler'],
    eventLinesClamp: ['eventLinesClamp'],
    timeHeader: ['timeHeader'],
    timeHeaderCell: ['timeHeaderCell'],
    timeHeaderDayLabel: ['timeHeaderDayLabel'],
    timeHeaderCellsRow: ['timeHeaderCellsRow'],
    timeHeaderTimeCell: ['timeHeaderTimeCell'],
    timeHeaderTimeLabel: ['timeHeaderTimeLabel'],
    daysHeader: ['daysHeader'],
    daysHeaderCell: ['daysHeaderCell'],
    daysHeaderTime: ['daysHeaderTime'],
    daysHeaderWeekDay: ['daysHeaderWeekDay'],
    daysHeaderDayNumber: ['daysHeaderDayNumber'],
    daysHeaderMonthStart: ['daysHeaderMonthStart'],
    daysHeaderMonthStartLabel: ['daysHeaderMonthStartLabel'],
    weeksHeader: ['weeksHeader'],
    weeksHeaderCell: ['weeksHeaderCell'],
    weeksHeaderDayLabel: ['weeksHeaderDayLabel'],
    weeksHeaderDaysRow: ['weeksHeaderDaysRow'],
    weeksHeaderDayCell: ['weeksHeaderDayCell'],
    monthsHeader: ['monthsHeader'],
    monthsHeaderYearLabel: ['monthsHeaderYearLabel'],
    monthsHeaderMonthLabel: ['monthsHeaderMonthLabel'],
    yearsHeader: ['yearsHeader'],
    yearsHeaderYearLabel: ['yearsHeaderYearLabel']
  }, eventDialogSlots);
  return composeClasses(slots, getEventTimelinePremiumUtilityClass, classes);
};
const EventTimelinePremiumRoot = styled('div', {
  name: 'MuiEventTimeline',
  slot: 'Root'
})(({
  theme
}) => ({
  '--time-cell-width': '64px',
  '--days-cell-width': '120px',
  '--weeks-cell-width': 'calc(64px * 7)',
  // Months view uses per-day units instead of per-month, so each column width = days in month × 6px
  '--months-cell-width': '6px',
  '--years-cell-width': '200px',
  boxSizing: 'border-box',
  '*, *::before, *::after': {
    boxSizing: 'inherit'
  },
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  height: '100%',
  minHeight: 0,
  overflow: 'hidden',
  fontFamily: theme.typography.fontFamily,
  fontSize: theme.typography.body2.fontSize
}));
export const EventTimelinePremium = /*#__PURE__*/React.forwardRef(function EventTimelinePremium(inProps, forwardedRef) {
  // We don't want the plan suffix in the theme, otherwise we couldn't share the theme entry across packages
  // eslint-disable-next-line mui/material-ui-name-matches-component-name
  const props = useThemeProps({
    props: inProps,
    name: 'MuiEventTimeline'
  });
  useLicenseVerifier(packageInfo);
  const _useExtractEventTimel = useExtractEventTimelinePremiumParameters(props),
    {
      parameters,
      forwardedProps: {
        className,
        classes: classesProp
      }
    } = _useExtractEventTimel,
    forwardedProps = _objectWithoutPropertiesLoose(_useExtractEventTimel.forwardedProps, _excluded);
  const store = useEventTimelinePremium(parameters);
  const classes = useUtilityClasses(classesProp);
  const {
      localeText,
      resourceColumnLabel,
      apiRef
    } = forwardedProps,
    other = _objectWithoutPropertiesLoose(forwardedProps, _excluded2);
  useInitializeApiRef(store, apiRef);
  const mergedLocaleText = React.useMemo(() => _extends({}, EVENT_TIMELINE_DEFAULT_LOCALE_TEXT, localeText), [localeText]);
  const timelineStyledContextValue = React.useMemo(() => ({
    classes,
    localeText: mergedLocaleText,
    resourceColumnLabel
  }), [classes, mergedLocaleText, resourceColumnLabel]);
  const dialogStyledContextValue = React.useMemo(() => ({
    classes,
    localeText: mergedLocaleText
  }), [classes, mergedLocaleText]);
  return /*#__PURE__*/_jsx(SchedulerStoreContext.Provider, {
    value: store,
    children: /*#__PURE__*/_jsx(EventTimelinePremiumStyledContext.Provider, {
      value: timelineStyledContextValue,
      children: /*#__PURE__*/_jsx(EventDialogStyledContext.Provider, {
        value: dialogStyledContextValue,
        children: /*#__PURE__*/_jsxs(EventTimelinePremiumRoot, _extends({
          ref: forwardedRef,
          className: clsx(classes.root, className)
        }, other, {
          children: [/*#__PURE__*/_jsx(EventTimelinePremiumContent, {}), watermark]
        }))
      })
    })
  });
});
if (process.env.NODE_ENV !== "production") EventTimelinePremium.displayName = "EventTimelinePremium";